// @ts-nocheck
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { IconWrapper } from "@insite/mobius/Icon";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import {
    DescriptionWrapperStyleProps,
    FormFieldSizeVariant,
    FormFieldStyleProps,
} from "@store/MobiusOverrides/FormField/FormField";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { LiteralUnion } from "@toolkit/Framework/Types/Common";
import { ExtendedCssProps } from "@toolkit/Framework/Types/StyledTheme";
import { focusStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

export const formFieldVariantStyles = () => {
    return css<ExtendedCssProps<Partial<FormFieldStyleProps>>>`
        ${({ _sizeVariant, border }) => {
            const { height, padding, lineHeight, fontSize } = sizeVariantValues[_sizeVariant || "default"] as any;
            const horizontalPadding = border === "underline" ? 0 : padding;
            const verticalPadding = (height - constants.formField.borderWidth * 2 - fontSize * lineHeight) / 2;

            return css`
                padding: ${verticalPadding}px ${horizontalPadding}px;
                font-size: ${pxToRem(fontSize)};
                line-height: ${lineHeight};
            `;
        }}
    ` as StyledProp;
};

export const formFieldInputSelectDisabledStyles = (color: string, backgroundColor: string, borderColor?: string) => {
    return css<ExtendedCssProps<Partial<FormFieldStyleProps>>>`
        &:disabled,
        &.disabled {
            ${formFieldInputSelectIconColorStyles("button.disabled")}
            ${x => cssProp("border-color", borderColor, y => resolveColor(y, x.theme))}
            background-color: ${getColor(backgroundColor)};
            color: ${getColor(color)};
            cursor: not-allowed;
        }
    ` as StyledProp;
};

export const formFieldInputSelectIconColorStyles = (color: string, hoverColor?: string) => {
    return css<ExtendedCssProps<Partial<FormFieldStyleProps>>>`
        ~ a,
        ~ button {
            color: ${getColor(color)};

            &:hover {
                color: ${getColor(hoverColor || color)};
            }
        }

        ~ ${/* sc-selector */ IconWrapper} {
            color: ${getColor(color)};
        }

        ~ ${/* sc-selector */ LoadingSpinnerStyle} circle {
            stroke: ${getColor(color)};
        }
    ` as StyledProp;
};

export const formFieldInputSelectIconStyles = (color: string, hoverColor?: string) => {
    return css<ExtendedCssProps<Partial<FormFieldStyleProps>>>`
        ${formFieldInputSelectIconColorStyles(color, hoverColor)}

        ~ a,
        ~ button {
            ${focusStyles(true)}
            transition: ${transitionHoverStyles("color", "background-color")};

            ${({ _sizeVariant, border }) => {
                const borderRadius =
                    border === "rounded" ? sizeVariantValues[_sizeVariant || "default"].borderRadius : 0;

                return css`
                    &:not(#important) /* Override buttonFieldIconClickableStyles */ {
                        border-radius: 0 ${borderRadius}px ${borderRadius}px 0;
                    }
                `;
            }}
        }
    ` as StyledProp;
};

export const formFieldDescriptionWrapperStyles = (
    sizeVariant?: LiteralUnion<FormFieldSizeVariant> | undefined,
    includeSpacing?: boolean,
) => {
    return css<ExtendedCssProps<Partial<DescriptionWrapperStyleProps>>>`
        ${({ _sizeVariant }) => {
            const parsedSizeVariant = sizeVariant || _sizeVariant || "default";
            const { descriptionLineSpacing, descriptionLineHeight, descriptionFontSize } = (sizeVariantValues as any)[
                parsedSizeVariant
            ];

            return css`
                font-size: ${descriptionFontSize}px;
                line-height: ${descriptionLineHeight};
                ${includeSpacing ? `margin-top: ${descriptionLineSpacing}px;` : null}
                font-weight: ${x => x.theme.design.typography.weight.bold};

                ${TypographyStyle} + ${TypographyStyle} /* Error + Hint */ {
                    margin-top: ${Math.floor(descriptionLineSpacing * descriptionLineHeight - descriptionLineSpacing) *
                    2}px;
                }
            `;
        }}
    ` as StyledProp;
};

export const formFieldLabelStyles = () => {
    return css`
        ${({ theme }) => css`
            font-weight: ${theme.design.typography.weight.bold};
            line-height: ${theme.design.typography.lineHeight.semi};
            font-size: ${theme.design.typography.size.smaller /* Store Custom Code */};
            /* Store Custom Code - Start */
            color: ${getColor("text.accent")};
            /* Store Custom Code - End */
        `}
    `;
};

export const formFieldButtonOffset = (size: FormFieldSizeVariant = "default") => {
    return () => {
        const { fontSize, descriptionLineSpacing, descriptionLineHeight } = sizeVariantValues[size] as any;
        return `calc(${descriptionLineSpacing}px + (${descriptionLineHeight} * ${fontSize}px))`;
    };
};
